// src/Components/Modals/VerifyEmailToPurchaseModal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { startPurchaseReauthEmailCode, verifyPurchaseReauthEmailCode } from "../Utils/purchaseReauth.api";
import Modal from "../Ui/Modal";
import Icon from "../Ui/Icon";

const RESEND_SECONDS = 30;

type Props = {
  onClose: () => void;
  onVerified?: () => void;
  initialEmail?: string;
};

function normalizeEmail(email: unknown) {
  return String(email ?? "").trim().toLowerCase();
}

function isValidEmail(email: unknown) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email));
}

function onlyDigits(s: string) {
  return String(s || "").replace(/\D/g, "");
}

function maskEmail(email: string) {
  const safe = normalizeEmail(email);
  const [name, domain] = safe.split("@");
  if (!domain) return safe || "";
  return `${name.slice(0, 2)}${"*".repeat(Math.max(0, name.length - 2))}@${domain}`;
}

export default function VerifyEmailToPurchaseModal({ onClose, onVerified, initialEmail = "" }: Props) {
  const [email, setEmail] = useState(String(initialEmail || ""));
  const [sent, setSent] = useState(false);
  // reauth challenge id returned by /reauth/start
  const [purchaseAuthId, setPurchaseAuthId] = useState<string>("");
  const [codeDigits, setCodeDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string>("");
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const isEmailAlreadyInUse = useMemo(() => {
    const msg = String(error || "").toLowerCase();
    return msg.includes("already") && msg.includes("use");
  }, [error]);

  const code = useMemo(() => codeDigits.join(""), [codeDigits]);

  function resetCodeInputs() {
    setCodeDigits(["", "", "", "", "", ""]);
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  }

  function resetAll() {
    setSent(false);
    setPurchaseAuthId("");
    setCodeDigits(["", "", "", "", "", ""]);
    setError("");
  }

  async function sendCode() {
    setError("");
    const trimmed = normalizeEmail(email);
    try {
      setSending(true);
      // With an email on file the backend ignores the provided one; without
      // one it must be passed or the backend returns 403 EMAIL_REQUIRED.
      const start = await startPurchaseReauthEmailCode(trimmed || undefined);

      if (start?.requiresGuardian) {
        setSent(false);
        setPurchaseAuthId("");
        setError("This account requires parent or guardian approval.");
        return;
      }
      if (start?.alreadyAuthorized) {
        onVerified?.();
        onClose();
        return;
      }
      const id = String(start?.purchaseAuthId || "");
      if (!id) {
        setSent(false);
        setPurchaseAuthId("");
        setError("Couldn't start verification. Try again.");
        return;
      }
      setPurchaseAuthId(id);
      setSent(true);
      setCooldown(RESEND_SECONDS);
      resetCodeInputs();
    } catch (err: any) {
      const status = err?.response?.status;
      const reason = err?.response?.data?.reason;
      const msg = err?.response?.data?.message || err?.message || "Failed to send code.";

      if (status === 409 && reason === "EMAIL_ALREADY_IN_USE") {
        setSent(false);
        setPurchaseAuthId("");
        setCodeDigits(["", "", "", "", "", ""]);
        setError("This email is already linked to another Moonrock account. Sign into that account, or use a different email.");
        return;
      }
      if (status === 403 && reason === "EMAIL_REQUIRED") {
        setSent(false);
        setPurchaseAuthId("");
        setError("Enter your email, then tap Send code.");
        return;
      }
      if (status === 401 || status === 403) {
        setSent(false);
        setPurchaseAuthId("");
        setError(msg || "Session expired. Please log in again.");
        return;
      }
      setSent(false);
      setPurchaseAuthId("");
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  async function verifyCode(codeOverride?: string) {
    setError("");
    const finalCode = onlyDigits(codeOverride ?? code);
    if (finalCode.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    if (!purchaseAuthId) {
      setError("Send the code first.");
      return;
    }
    try {
      setVerifying(true);
      await verifyPurchaseReauthEmailCode(purchaseAuthId, finalCode);
      onVerified?.();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const reason = err?.response?.data?.reason;
      const msg = err?.response?.data?.message || err?.message || "Invalid or expired code.";
      if (status === 409 && reason === "EMAIL_ALREADY_IN_USE") {
        setSent(false);
        setPurchaseAuthId("");
        setError("This email is already linked to another Moonrock account. Sign into that account, or use a different email.");
        return;
      }
      if (status === 401) {
        setError("Invalid or expired code.");
        return;
      }
      if (status === 429) {
        setError("Too many attempts. Try again shortly.");
        return;
      }
      setError(msg);
    } finally {
      setVerifying(false);
    }
  }

  function handleDigitChange(index: number, raw: string) {
    setError("");
    const digits = onlyDigits(raw);
    if (digits.length >= 6) {
      const next = digits.slice(0, 6).split("");
      setCodeDigits(next);
      verifyCode(next.join(""));
      return;
    }
    if (digits.length > 1) {
      const next = [...codeDigits];
      let cursor = index;
      for (const d of digits) {
        if (cursor > 5) break;
        next[cursor] = d;
        cursor += 1;
      }
      setCodeDigits(next);
      if (cursor <= 5) inputRefs.current[cursor]?.focus();
      else verifyCode(next.join(""));
      return;
    }
    const next = [...codeDigits];
    next[index] = digits.slice(0, 1);
    setCodeDigits(next);
    if (digits.length === 1 && index < 5) inputRefs.current[index + 1]?.focus();
    if (next.every((d) => d.length === 1)) verifyCode(next.join(""));
  }

  function handleDigitKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (codeDigits[index]) {
        const next = [...codeDigits];
        next[index] = "";
        setCodeDigits(next);
        return;
      }
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const next = [...codeDigits];
        next[index - 1] = "";
        setCodeDigits(next);
      }
    }
    if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) inputRefs.current[index + 1]?.focus();
    if (e.key === "Enter") {
      e.preventDefault();
      verifyCode();
    }
  }

  const canSubmit = sent && !!purchaseAuthId && codeDigits.every((d) => d.length === 1) && !sending && !verifying;

  const emailOk = isValidEmail(email);
  return (
    <Modal
      open
      onClose={onClose}
      tone="dark"
      size="sm"
      eyebrow={sent ? "Step 2 of 2" : "Step 1 of 2"}
      title={sent ? "Enter your code" : "Verify your email"}
      className="vmail"
      footer={
        sent ? (
          <button type="button" className="mr-btn mr-btn--primary mr-btn--block" onClick={() => verifyCode()} disabled={!canSubmit} aria-busy={verifying}>
            {verifying ? "Checking..." : "Verify and continue"}
          </button>
        ) : (
          <button type="button" className="mr-btn mr-btn--primary mr-btn--block" onClick={sendCode} disabled={sending || !emailOk} aria-busy={sending}>
            {sending ? "Sending..." : "Send me a code"}
          </button>
        )
      }
    >
      <div className="vmail__body">
        <div className={`vmail__hero ${sent ? "is-sent" : ""}`} aria-hidden="true">
          <span className="vmail__glow" />
          <span className="vmail__icon"><Icon name={sent ? "check" : "mail"} size={30} /></span>
        </div>

        {!sent ? (
          <>
            <p className="vmail__lead">Before your first purchase we check it is really you. We will email you a 6-digit code.</p>
            <label className="vmail__label" htmlFor="verify-email">Email address</label>
            <input
              id="verify-email"
              className="vmail__input"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); resetAll(); }}
              onKeyDown={(e) => { if (e.key === "Enter" && emailOk && !sending) { e.preventDefault(); sendCode(); } }}
              placeholder="you@example.com"
              inputMode="email"
              autoComplete="email"
              autoFocus
              disabled={sending}
            />
          </>
        ) : (
          <>
            <p className="vmail__lead">
              We sent a code to <b>{maskEmail(email)}</b>. It can take a minute to arrive, so check your spam folder too.
            </p>
            <div className="vmail__code" role="group" aria-label="Verification code">
              {codeDigits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  className={`vmail__digit ${d ? "is-filled" : ""} ${error ? "is-error" : ""}`}
                  value={d}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  aria-label={`Digit ${i + 1}`}
                  disabled={verifying}
                  maxLength={6}
                />
              ))}
            </div>
            <div className="vmail__actions">
              <button type="button" className="vmail__link" onClick={sendCode} disabled={sending || cooldown > 0}>
                {sending ? "Sending..." : cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
              <span aria-hidden="true">|</span>
              <button type="button" className="vmail__link" onClick={resetAll} disabled={verifying}>
                Change email
              </button>
            </div>
          </>
        )}

        {error ? <div className="vmail__error" role="alert">{error}</div> : null}
        {isEmailAlreadyInUse && <div className="vmail__help">Think this is a mistake? Email support@playmoonrock.com</div>}
      </div>
    </Modal>
  );
}
